// HubDB read helper.
//
// HubDB tables are CENTRALIZED in the production portal (they 404 under sandbox),
// so reads always target the fixed `window.__ENV__.dataPortalId`, and table ids
// come from `window.__ENV__.hubdb.*` — never numeric literals (see AGENTS.md).
//
// These are cross-portal reads against api.hubapi.com (an absolute host by
// necessity), distinct from the theme's own root-relative `/_hcms/api/*` calls.

const HUBDB_HOST = "https://api.hubapi.com"

type HubdbTableKey = keyof Window["__ENV__"]["hubdb"]
type QueryValue = string | number

/** Build the HubDB v2 rows URL for a table key, resolving id + portal from __ENV__. */
export function hubdbRowsUrl(tableKey: HubdbTableKey, query: Record<string, QueryValue> = {}): string {
  const tableId = window.__ENV__.hubdb[tableKey]
  const params = new URLSearchParams()
  params.set("portalId", window.__ENV__.dataPortalId)
  for (const [key, value] of Object.entries(query)) {
    params.set(key, String(value))
  }
  return `${HUBDB_HOST}/hubdb/api/v2/tables/${tableId}/rows?${params.toString()}`
}

/** Build the HubDB v2 URL for a SINGLE row by id, resolving id + portal from __ENV__. */
export function hubdbRowUrl(
  tableKey: HubdbTableKey,
  rowId: string | number,
  query: Record<string, QueryValue> = {},
): string {
  const tableId = window.__ENV__.hubdb[tableKey]
  const params = new URLSearchParams()
  params.set("portalId", window.__ENV__.dataPortalId)
  for (const [key, value] of Object.entries(query)) {
    params.set(key, String(value))
  }
  return `${HUBDB_HOST}/hubdb/api/v2/tables/${tableId}/rows/${rowId}?${params.toString()}`
}

export interface HubdbRow {
  id: string | number
  values: Record<string, unknown>
}

/** GET rows for a table key. Returns the `objects` array (empty on any failure). */
export async function hubdbGetRows(
  tableKey: HubdbTableKey,
  query: Record<string, QueryValue> = {},
): Promise<HubdbRow[]> {
  const response = await fetch(hubdbRowsUrl(tableKey, query), { method: "GET" })
  const data = await response.json()
  return (data?.objects ?? []) as HubdbRow[]
}
